/*  Shared  */
import { Skeleton } from '@mui/material';
import { TagChip } from 'application/Shared/Components/Tag/TagChip';

/* Hooks */
import {
    useFetchGlobalTagsAsync,
    useFetchTagLinksAsync,
    useFetchTagSettingsAsync
} from 'hooks/dataAccess/GlobalTag';

/*  React  */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';

/*  Components  */
import ObjectPicker from './Components/ObjectPicker';

const makePayload = ({ globalTags, tagLinks, tagSettings, nameOnly }) => {
    /*
        Based on loading all auvikManaged ('default') global tags, all of the tenant's custom tag setting,
        and any tag links they've created, we compile a list to provide them with any tag they should be able to assign.

        We normalize them into objects of:
        {
            id (globalTag's ID)
            color (set on the tagSetting, with globalTag's defaultColor as fallback)
            name (set on the globalTag)
        }

        We build the list with the following priority
        tenant's tagSettings > tenant's tagLinks > globalTags


        if we want to leverage description at some point, we could always add that here.
    */
    const tagObjectsByGlobalTagId = {};

    tagSettings?.forEach((tagSetting) => {
        if (!tagObjectsByGlobalTagId[tagSetting.globalTagId]) {
            tagObjectsByGlobalTagId[tagSetting.globalTagId] = {
                color: tagSetting.color || tagSetting?.globalTag?.defaultColor,
                id: tagSetting?.globalTag?.id,
                name: tagSetting?.globalTag?.name
            };
        }
    });

    tagLinks?.forEach((tagLink) => {
        if (!tagObjectsByGlobalTagId[tagLink.globalTagId]) {
            tagObjectsByGlobalTagId[tagLink.globalTagId] = {
                color: tagLink.color || tagLink?.globalTag?.defaultColor,
                id: tagLink?.globalTag?.id,
                name: tagLink?.globalTag?.name
            };
        }
    });

    globalTags?.forEach((tag) => {
        if (!tagObjectsByGlobalTagId[tag.id]) {
            tagObjectsByGlobalTagId[tag.id] = {
                color: tag.defaultColor,
                id: tag.id,
                name: tag.name
            };
        }
    });

    const deduplicatedTags = Object.values(tagObjectsByGlobalTagId);

    const sortedTags = deduplicatedTags.sort((a, b) => {
        if (a.name < b.name) {
            return -1;
        }
        if (a.name > b.name) {
            return 1;
        }
        return 0;
    });

    return sortedTags.map((tag) => {
        const { color, name } = tag;

        return {
            ...tag,
            displayVisualOnly: nameOnly ? (
                tag.name
            ) : (
                <TagChip name={name} color={color} />
            )
        };
    });
};

/**
 * TagsPicker - A component for selecting tags from global tags, tag settings, and tag links
 *
 * This component fetches and displays available tags from multiple sources:
 * - Global tags (Auvik managed tags)
 * - Tag settings (tenant-specific tag customizations)
 * - Tag links (application-specific tag associations)
 *
 * Tags are displayed with priority: tag settings > tag links > global tags
 *
 * @param {Object} props - Component props
 * @param {string} [props.applicationId] - Optional application ID for filtering tag links.
 *   When provided, only tag links associated with this application will be included.
 *   When omitted, all tag links for the current tenant will be included.
 * @param {boolean} [props.disableAllOption] - Whether to disable the "All" option in the picker
 * @param {boolean} [props.visualOnly] - If true, displays only tag names instead of styled tag chips
 * @param {boolean} [props.multiple] - If true, allows multiple tags to be selected
 * @param {string} [props.allValue] - Text to display for the all option
 * @param {boolean} [props.nameOnly] - If true, displays only tag names instead of styled tag chips
 * @param {string} [props.allValueId] - Id of the all option
 * @param {Object} [props.onChange] - OnChangeFunction
 * @param {Array<string>} [props.value] - Value to use for the selected tags
 * @param {string} [props.unselectedOption] - Text to display for the unselected option
 * @param {string | string[]} [props.unselectedValue] - Value to use for the unselected option
 * @param {boolean} [props.includeGlobalTags=true] - Whether to include global tags in the picker
 * @param {boolean} [props.includeTagLinks=true] - Whether to include tag links in the picker
 * @param {boolean} [props.includeTagSettings=true] - Whether to include tag settings in the picker
 * @param {Object} [props.rest] - Additional props passed to the underlying ObjectPicker component
 *
 * @returns {JSX.Element} A tag picker component with loading skeleton or ObjectPicker
 *
 * @example
 * // Basic usage with all tag types
 * <TagsPicker
 *   onChange={handleTagChange}
 *   value={selectedTags}
 * />
 *
 * @example
 * // Filter tags for a specific application
 * <TagsPicker
 *   applicationId="app-123"
 *   onChange={handleTagChange}
 *   value={selectedTags}
 * />
 *
 * @example
 * // Only show global tags and tag settings (no tag links)
 * <TagsPicker
 *   includeTagLinks={false}
 *   onChange={handleTagChange}
 *   value={selectedTags}
 * />
 */
export const TagsPicker = ({
    applicationId,
    disableAllOption,
    unselectedOption,
    unselectedValue,
    nameOnly,
    includeGlobalTags = true,
    includeTagLinks = true,
    includeTagSettings = true,
    ...rest
}) => {
    const { clientId, partnerId } = useParams();
    const platformTenantId = clientId || partnerId;

    const [loaded, setLoaded] = useState(false);
    const [objectPayload, setObjectPayload] = useState([]);

    const { getGlobalTags } = useFetchGlobalTagsAsync();
    const { getTagLinks } = useFetchTagLinksAsync();
    const { getTagSettings } = useFetchTagSettingsAsync();

    useEffect(() => {
        const fetchTagsData = async () => {
            let globalTags = [];
            let tagLinks = [];
            let tagSettings = [];

            if (includeGlobalTags) {
                const globalTagsParams = {
                    auvikManaged: true,
                    size: -1
                };
                globalTags = await getGlobalTags(globalTagsParams);
            }

            if (includeTagSettings) {
                const tagSettingsParams = {
                    includeTag: true,
                    mergeClientAndPartner: true,
                    platformTenantId,
                    size: -1
                };
                tagSettings = await getTagSettings(tagSettingsParams);
            }

            if (includeTagLinks) {
                const tagLinkParams = {
                    applicationId,
                    includeTag: true,
                    includeTagSettings: true,
                    mergeClientAndPartner: true,
                    platformTenantId,
                    size: -1
                };
                tagLinks = await getTagLinks(tagLinkParams);
            }

            const tagsToDisplay = makePayload({
                globalTags,
                nameOnly,
                tagLinks,
                tagSettings
            });

            setObjectPayload(tagsToDisplay);
            setLoaded(true);
        };

        fetchTagsData();
    }, [
        applicationId,
        getGlobalTags,
        getTagLinks,
        getTagSettings,
        includeGlobalTags,
        includeTagLinks,
        includeTagSettings,
        nameOnly,
        platformTenantId
    ]);

    if (!loaded) {
        return <Skeleton animation="wave" height="40px" />;
    }

    return (
        <ObjectPicker
            {...rest}
            multiple
            objectPayload={objectPayload}
            objectNameField="name"
            objectNameFieldVisualOnly="displayVisualOnly"
            disableAllOption={disableAllOption}
            unselectedOption={unselectedOption}
            unselectedValue={unselectedValue}
            disableClearable
        />
    );
};
